import { useState, useEffect } from 'react'
import Cards from '../../components/Cards'
import { Button } from '../../components/Buttons'
import { toast } from '../../components/Toast/toast'
import { CopyIcon } from '../../components/Icons/iconImages'
import referralsImage from '../../assets/images/background/referrals.webp'
import { useGenerateReferralCodeMutation } from '../../services/authSlice'

interface ReferralData {
  message: string
  referral_code: string
  referral_url: string
  created_at: string
  total_referrals: number
}

function Referrals() {
  const [copying, setCopying] = useState(false)
  const [referralData, setReferralData] = useState<ReferralData | null>(null)
  const [isCodeVisible, setIsCodeVisible] = useState(false)
  const [generateReferralCode, { isLoading: isGeneratingCode }] = useGenerateReferralCodeMutation()

  // Fetch existing referral code on mount
  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        const response = await generateReferralCode({}).unwrap() as ReferralData
        if (response?.referral_code) {
          setReferralData(response)
        }
      } catch (error) {
        console.error('Failed to fetch referral data:', error)
      }
    }

    fetchReferralData()
  }, [generateReferralCode])

  const referralLink = `${window.location.origin}/signup?ref=${referralData?.referral_code || ''}`

  const handleCopyReferralLink = async () => {
    try {
      setCopying(true)

      // Generate or fetch existing referral code
      const response = await generateReferralCode({}).unwrap() as ReferralData

      if (response?.referral_code) {
        setReferralData(response)
        const linkToCopy = `${window.location.origin}/signup?ref=${response.referral_code}`
        await navigator.clipboard.writeText(linkToCopy)
        toast.success(response.message || 'Referral link copied to clipboard!')
      }
    } catch (error) {
      console.error('Failed to generate/copy referral code:', error)
      toast.error('Failed to copy referral link')
    } finally {
      setCopying(false)
    }
  }

  const handleInviteNow = async () => {
    try {
      // Generate referral code if not already generated
      let dataToUse = referralData
      if (!referralData) {
        const response = await generateReferralCode({}).unwrap() as ReferralData
        if (response?.referral_code) {
          setReferralData(response)
          dataToUse = response
        }
      }

      const linkToShare =  referralLink

      const subject = encodeURIComponent('One Click = One Free Month 👇')
      const body = encodeURIComponent(`Here's an easy win.

Invite a friend, colleague, or client to try Oogwai — the platform that turns marketing analytics into clarity.

When they sign up, you both enjoy one free month of Oogwai access.

No limits, no hassle — just rewards for spreading something useful.

Ready to grow your circle of smart marketers?

👉 Get Your Referral Link & Earn:
${linkToShare}`)

      // Use window.location.href for better compatibility (more reliable than window.open for mailto)
      const mailtoLink = `mailto:?subject=${subject}&body=${body}`
      window.location.href = mailtoLink

      // Show success feedback
      toast.success('Opening your email client...')
    } catch (error) {
      console.error('Failed to generate referral code:', error)
      toast.error('Failed to generate referral link')
    }
  }

  return (
    <Cards
      variant="secondary"
      classname="p-3 sm:p-4 md:p-6"
      childClassName="flex-col"
      title=""
      bottomButton={undefined}
    >
      <div className="flex w-full justify-between items-start border-b border-solid border-gray-300 pb-3 sm:pb-4">
        <div>
          <h4 className="text-[#181D27] font-semibold text-base md:text-lg">Refer</h4>
          <h5 className="font-normal text-[#535862] text-xs md:text-sm">
            Share Oogwai with friends and colleagues.
          </h5>
        </div>
      </div>

      {/* Referral Section */}
      <div className="my-4 grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
        {/* Left Side - Text and Buttons */}
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#181D27] leading-tight">
              Give 1 month. Get 1 month.
            </h2>
          </div>

          <div>
            <p className="text-sm md:text-base text-[#535862]">
              Share Oogwai with your Network. When they join, you both get 1 month free.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleCopyReferralLink}
              loading={copying}
              disabled={copying}
              className="inline-flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <CopyIcon className="w-4 h-4" />
              Copy Referral Link
            </Button>

            <Button
              type="primary"
              onClick={handleInviteNow}
              loading={isGeneratingCode && !referralData}
              disabled={isGeneratingCode && !referralData}
              // disabled={true}
              className="inline-flex items-center justify-center gap-2"
            >
              Invite Now
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </Button>
          </div>
        </div>

        {/* Right Side - Image */}
        <div className="relative overflow-hidden">
          <img
            src={referralsImage}
            alt="Team collaboration with city skyline"
            className="w-full h-[592px] lg:h-[592px] object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        </div>
      </div>

      {/* Referral Stats Section */}
      {referralData && (
        <div className="mt-8 pt-6 border-t border-gray-300">
          <h4 className="text-[#181D27] font-semibold text-base mb-4">Your Referral Stats</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs text-[#535862] mb-1">Referral Code</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-bold text-[#181D27]">
                  {isCodeVisible ? referralData.referral_code : '••••••••'}
                </p>
                <button
                  onClick={() => setIsCodeVisible(!isCodeVisible)}
                  className="text-[#535862] hover:text-[#181D27] transition-colors"
                  aria-label={isCodeVisible ? 'Hide referral code' : 'Show referral code'}
                >
                  {isCodeVisible ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {/* <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs text-[#535862] mb-1">Total Referrals</p>
              <p className="text-2xl font-bold text-[#181D27]">{referralData.total_referrals || 0}</p>
            </div> */}
          </div>
        </div>
      )}
    </Cards>
  )
}

export default Referrals
